import en from './en';
import newLanding from './newLanding';
import lyricsVideo from './lyricsVideo';

// Language display metadata. Deliberately free of the heavy per-language
// translation files so that importing language names (for the selector UI or
// path routing) does not pull ~1MB of translation payload into the bundle.
const LANGUAGE_META = {
  en: { name: 'English', nativeName: 'English' },
  es: { name: 'Spanish', nativeName: 'Español' },
  ja: { name: 'Japanese', nativeName: '日本語' },
  pt: { name: 'Portuguese', nativeName: 'Português' },
  fr: { name: 'French', nativeName: 'Français' },
  zh: { name: 'Chinese', nativeName: '简体中文' },
  ko: { name: 'Korean', nativeName: '한국어' },
  id: { name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  it: { name: 'Italian', nativeName: 'Italiano' },
  nl: { name: 'Dutch', nativeName: 'Nederlands' },
  pl: { name: 'Polish', nativeName: 'Polski' },
  tr: { name: 'Turkish', nativeName: 'Türkçe' },
  vi: { name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  de: { name: 'German', nativeName: 'Deutsch' },
} as const;

export type LanguageCode = keyof typeof LANGUAGE_META;

export const supportedLanguages = Object.keys(LANGUAGE_META) as LanguageCode[];

export const languageNames = Object.entries(LANGUAGE_META).map(([code, { nativeName }]) => ({
  code,
  name: nativeName,
}));

const newLandingByCode = newLanding as Record<string, unknown>;
const lyricsVideoByCode = lyricsVideo as Record<string, unknown>;

// Merge a per-language base translation with the shared newLanding +
// lyricsVideo namespaces (single files keyed by language code, kept bundled so
// English — the fallback — and every other language resolve them instantly).
const withExtras = (translation: Record<string, unknown>, code: string) => ({
  ...translation,
  newLanding: newLandingByCode[code] ?? newLanding.en,
  lyricsVideo: lyricsVideoByCode[code] ?? lyricsVideo.en,
});

// English is bundled synchronously: it is both the fallback language and the
// most common visitor language, so it must be present on first paint with no
// async round-trip.
export const enResource = withExtras(en as Record<string, unknown>, 'en');

// Every other language is code-split into its own chunk and fetched on demand
// by i18n's lazy backend (see i18n.ts). This is what keeps the big translation
// files out of the entry bundle.
const loaders: Record<
  Exclude<LanguageCode, 'en'>,
  () => Promise<{ default: Record<string, unknown> }>
> = {
  es: () => import('./es'),
  ja: () => import('./ja'),
  pt: () => import('./pt'),
  fr: () => import('./fr'),
  zh: () => import('./zh'),
  ko: () => import('./ko'),
  id: () => import('./id'),
  it: () => import('./it'),
  nl: () => import('./nl'),
  pl: () => import('./pl'),
  tr: () => import('./tr'),
  vi: () => import('./vi'),
  de: () => import('./de'),
};

// Loads and merges the full translation resource for a language. Returns null
// for unknown codes so the caller can fall back to English.
export async function loadLanguageResource(
  code: string,
): Promise<Record<string, unknown> | null> {
  if (code === 'en') return enResource;
  const loader = loaders[code as Exclude<LanguageCode, 'en'>];
  if (!loader) return null;
  const mod = await loader();
  return withExtras(mod.default, code);
}
